import React, { FunctionComponent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled, { createGlobalStyle } from 'styled-components';
import {
  removeAdditionalInsured,
  clearAdditionalInsured,
  didViewAdditionalInsureds,
  setUserSelectedHasAdditionalInsureds,
  removeBlanketAdditionalInsureds,
} from '../actions/application';
import {
  selectAdditionalInsureds,
  selectApplication,
  selectUserHasSelectedAdditionalInsureds,
  selectUserHasViewedAdditionalInsuredsPage,
} from '../selectors/application';
import AdditionalInsuredsForm, { AdditionalInsuredPolicyType } from '../Components/Shared/AdditionalInsuredsForm';
import DeleteOrUpdateButton, { Button } from '../Components/Shared/DeleteOrUpdateButton';
import { Paragraph, Container, FormSection } from '../elements';
import SmoothCollapse from 'react-smooth-collapse';
import GhostButton from '../Components/Shared/GhostButton';
import handleFormSubmit from '../actions/handleFormSubmit';
import RadioBtn from '../elements/RadioBtn';
import BlanketAdditionalInsureds from '../Components/AdditionalInsureds/BlanketAdditionalInsureds';
import { PolicyType } from '../types/enums';
import uniqBy from 'lodash/uniqBy';
import { useFeatureFlags } from '../toggle_tools/featureFlagTools';
import checkmark from '../icons/checkmark_blue_icon.svg';

interface AdditionalInsuredsProps {
  additionalInsureds: AdditionalInsured[];
  additionalInsuredsPl?: AdditionalInsured[];
  additionalInsuredsScreenViewed: boolean;
  removeAdditionalInsured: (name: string) => void;
  didViewAdditionalInsureds: () => void;
  clearAdditionalInsured: () => void;
  setShowAdditionalInsuredsForm: (bool: boolean) => void;
  removeBlanketAdditionalInsureds: () => void;
  shouldShowAdditionalInsuredsForm: boolean;
  applicationTypes?: PolicyType[];
}

export const AdditionalInsureds: FunctionComponent<AdditionalInsuredsProps> = ({
  additionalInsureds,
  additionalInsuredsPl,
  additionalInsuredsScreenViewed,
  removeAdditionalInsured,
  didViewAdditionalInsureds,
  clearAdditionalInsured,
  setShowAdditionalInsuredsForm,
  shouldShowAdditionalInsuredsForm,
  removeBlanketAdditionalInsureds,
  applicationTypes,
}) => {
  const { showPlv2 } = useFeatureFlags();
  const [policyType, setPolicyType] = useState<AdditionalInsuredPolicyType>();
  const isOnlyPL = applicationTypes?.length === 1 && applicationTypes[0] === PolicyType.PL;
  const insureds = showPlv2
    ? uniqBy([...additionalInsureds, ...(additionalInsuredsPl || [])], 'name')
    : uniqBy(additionalInsureds, 'name');

  useEffect(() => {
    if (additionalInsuredsScreenViewed && Array.isArray(insureds)) setShowAdditionalInsuredsForm(false);
    if (additionalInsureds && additionalInsureds.length) setShowAdditionalInsuredsForm(true);
    // TODO refactor this logic to not depend on redux; we shouldn't need this useEffect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOnlyPL) removeBlanketAdditionalInsureds();
  }, [isOnlyPL, removeBlanketAdditionalInsureds]);

  const deleteAddlIns = (name: string) => {
    removeAdditionalInsured(name);
  };

  const submitAdditionalInsured = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleFormSubmit();
  };

  const showAdditionalInsuredsForm = () => {
    didViewAdditionalInsureds();
    setShowAdditionalInsuredsForm(true);
  };

  const hideAdditionalInsuredsForm = () => {
    clearAdditionalInsured();
    didViewAdditionalInsureds();
    setShowAdditionalInsuredsForm(false);
    setPolicyType(undefined);
  };

  return (
    <Container>
      <StyledFormSection>
        <StyledParagraph>
          Additional insureds are anyone other than the policyholder covered by the policy. You can add multiple
          additional insureds to your policy.
        </StyledParagraph>
        {!isOnlyPL && <BlanketAdditionalInsureds />}
        <RadioContainer data-cy="individual-additional-insureds" data-testid="individual-additional-insureds">
          <RadioText>Would you like to add an individual additional insured?</RadioText>

          <RadioBtn
            id="yes-individual"
            dataCy="yes-additional-insureds"
            labelText="Yes"
            isChecked={shouldShowAdditionalInsuredsForm}
            onChange={showAdditionalInsuredsForm}
            name="individual-additional-insured"
          />
          <RadioBtn
            id="no-individual"
            dataCy="no-additional-insureds"
            labelText="No"
            isChecked={typeof shouldShowAdditionalInsuredsForm === 'boolean' && !shouldShowAdditionalInsuredsForm}
            onChange={hideAdditionalInsuredsForm}
            name="individual-additional-insured"
          />
        </RadioContainer>
      </StyledFormSection>

      <Form onSubmit={submitAdditionalInsured} data-cy="additional-insureds-section">
        <div>
          {insureds
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((insured) => {
              const isPl = additionalInsuredsPl?.find((plInsured) => plInsured.name === insured.name);
              const isBopGl = additionalInsureds.find((bopGlInsured) => bopGlInsured.name === insured.name);
              const isBoth = isPl && isBopGl;
              return (
                <ExistingAddlIns data-cy="additional-insured" key={insured.name}>
                  <AddlInsContent>
                    <p>{insured.name}</p>
                    <p>{insured.email}</p>
                    <p>
                      {insured.street}, {insured.city} {insured.state}
                    </p>
                    <AddedMessage>
                      <img src={checkmark as string} alt="checkmark" />
                      {isBoth && applicationTypes?.includes(PolicyType.BOP) && (
                        <span>Added to BOP and PL policies</span>
                      )}
                      {isBoth && applicationTypes?.includes(PolicyType.GL) && <span>Added to GL and PL policies</span>}
                      {isPl && !isBoth && <span>Added to PL policy</span>}
                      {isBopGl && applicationTypes?.includes(PolicyType.BOP) && !isBoth && (
                        <span>Added to BOP policy</span>
                      )}
                      {isBopGl && applicationTypes?.includes(PolicyType.GL) && !isBoth && (
                        <span>Added to GL policy</span>
                      )}
                    </AddedMessage>
                  </AddlInsContent>
                  <OverrideStyle />
                  <DeleteOrUpdateButton
                    handleClick={() => deleteAddlIns(insured.name)}
                    type="delete"
                    name="additional-insureds"
                  />
                </ExistingAddlIns>
              );
            })}
        </div>
        <SmoothCollapse expanded={Boolean(shouldShowAdditionalInsuredsForm)} allowOverflowWhenOpen={true}>
          <AdditionalInsuredsForm setPolicyType={setPolicyType} policyType={policyType} />
        </SmoothCollapse>
        <GhostButton />
      </Form>
    </Container>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  additionalInsureds: selectAdditionalInsureds(state),
  additionalInsuredsPl: selectApplication(state).professionalLiability?.endorsements?.additionalInsureds,
  additionalInsuredsScreenViewed: selectUserHasViewedAdditionalInsuredsPage(state),
  shouldShowAdditionalInsuredsForm: selectUserHasSelectedAdditionalInsureds(state),
  applicationTypes: selectApplication(state).applicationTypes,
});

const mapDispatchToProps = (dispatch: any) => ({
  removeAdditionalInsured: (name: string) => dispatch(removeAdditionalInsured(name)),
  clearAdditionalInsured: () => dispatch(clearAdditionalInsured()),
  didViewAdditionalInsureds: () => dispatch(didViewAdditionalInsureds()),
  setShowAdditionalInsuredsForm: (bool: boolean) => dispatch(setUserSelectedHasAdditionalInsureds(bool)),
  removeBlanketAdditionalInsureds: () => dispatch(removeBlanketAdditionalInsureds()),
});

export const RadioContainer = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 30px;

  color: ${({ theme }) => theme.colors.tertiary.zero};
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
`;

const Form = styled.form`
  margin-bottom: 72px;
`;

export const RadioText = styled.legend`
  margin-bottom: 16px;
`;

const ExistingAddlIns = styled.div`
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  border: ${(props) => props.theme.pageComponents.additionalInsureds.existingAddIns.border};
  border-radius: 4px;
  margin-bottom: 16px;
`;

const AddlInsContent = styled.div`
  padding: 13px 16px;

  p {
    font-weight: normal;
    margin: 0;
    margin-bottom: 3px;
    font-size: 15px;
    font-family: ${(props) => props.theme.font.typeface.secondary};
  }
`;

const OverrideStyle = createGlobalStyle`
  button${Button} {
    margin-top: -20px;
    margin-right: 15px;
  }
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: 32px;
  font-size: 16px;
  font-family: ${(props) => props.theme.font.typeface.secondary};
`;

const AddedMessage = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px !important;
  margin-top: 8px !important;
`;

const StyledFormSection = styled(FormSection)`
  margin-bottom: 0px;
`;

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalInsureds);
